/**
 * useProjectionsSummary Hook
 *
 * React hook for fetching company-wide budget projections summary.
 * Includes loading states, error handling, and in-memory caching.
 *
 * @module hooks/useProjectionsSummary
 * @description Provides company-wide financial projections data for a specific year.
 * Caches data by year to avoid redundant API calls within the same session.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { getProjectionsSummary } from '@/api/budgets'
import type { ProjectionsSummary } from '@/types/budgets'

/**
 * Hook state interface
 */
interface UseProjectionsSummaryResult {
  /** Projections data or null if not loaded */
  data: ProjectionsSummary | null
  /** True when fetching data from API */
  loading: boolean
  /** Error object if fetch failed, null otherwise */
  error: Error | null
  /** Function to manually refresh data (bypasses cache) */
  refetch: () => Promise<void>
}

/**
 * Cache structure: year -> ProjectionsSummary
 */
type ProjectionsSummaryCache = Record<number, ProjectionsSummary>

/**
 * Fetch and cache company-wide budget projections summary
 *
 * @param year - Budget year to fetch projections for
 * @returns Hook state with data, loading, error, and refetch function
 *
 * @example
 * function ProjectionsPage() {
 *   const { data, loading, error, refetch } = useProjectionsSummary(2025)
 *
 *   if (loading) return <div>Loading projections...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *   if (!data) return <div>No projections found</div>
 *
 *   return (
 *     <div>
 *       <h2>Projections for {data.year}</h2>
 *       <p>Total Revenue: {data.projections.totalRevenue}</p>
 *       <p>Client Count: {data.clientCount}</p>
 *       <button onClick={refetch}>Refresh</button>
 *     </div>
 *   )
 * }
 */
export function useProjectionsSummary(year: number): UseProjectionsSummaryResult {
  // State management
  const [data, setData] = useState<ProjectionsSummary | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  // In-memory cache (persists across re-renders but not page reloads)
  const cache = useRef<ProjectionsSummaryCache>({})

  /**
   * Fetch projections data from API or cache
   *
   * @param bypassCache - If true, ignores cache and forces fresh fetch
   */
  const fetchData = useCallback(
    async (bypassCache: boolean = false) => {
      try {
        setLoading(true)
        setError(null)

        // Check cache first (unless bypassing)
        if (!bypassCache && cache.current[year]) {
          setData(cache.current[year])
          setLoading(false)
          return
        }

        // Fetch from API
        const result = await getProjectionsSummary(year)

        // Update cache and state
        cache.current[year] = result
        setData(result)
        setError(null)
      } catch (err) {
        // Handle errors
        const error = err instanceof Error ? err : new Error('Failed to fetch projections summary')
        setError(error)
        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [year]
  )

  /**
   * Refetch function for manual refresh
   * Bypasses cache and fetches fresh data from API
   */
  const refetch = useCallback(async () => {
    // Clear cache for this year before refetching
    delete cache.current[year]
    await fetchData(true)
  }, [year, fetchData])

  // Fetch data when year changes
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    loading,
    error,
    refetch,
  }
}
